import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckSquare, Square, Filter } from 'lucide-react';
import axios from 'axios';

const SurveySelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  // Get ecId from either URL params or location state
  const ecIdFromParams = params.ecId;
  const { ecId: ecIdFromState, extractedData } = location.state || {};
  const ecId = ecIdFromParams || ecIdFromState;
  
  const [surveyNumbers, setSurveyNumbers] = useState([]);
  const [selectedSurveys, setSelectedSurveys] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ecData, setEcData] = useState(extractedData);

  useEffect(() => {
    if (!ecId) {
      navigate('/ec-extractor');
      return;
    }

    // If we don't have extracted data, fetch it
    if (!ecData) {
      fetchECData();
    } else {
      processECData(ecData);
    }
  }, [ecId, ecData, navigate, location.state]);

  const fetchECData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ec/${ecId}`
      );
      
      if (response.data.success) {
        const fetchedData = response.data.ec_data.extracted_data;
        setEcData(fetchedData);
        processECData(fetchedData);
      } else {
        throw new Error('Failed to fetch EC data');
      }
    } catch (err) {
      console.error('Error fetching EC data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load EC data');
    } finally {
      setLoading(false);
    }
  };

  const processECData = (data) => {

    // Extract unique survey numbers from all transactions
    const transactions = data.transactions || [];
    const uniqueSurveys = new Set();
    
    transactions.forEach(txn => {
      const survey = txn.survey_number;
      if (survey && survey !== 'N/A' && survey !== null) {
        // Split multiple survey numbers (e.g., "201, 202, 203")
        const surveyParts = survey.split(',').map(s => s.trim()).filter(s => s);
        surveyParts.forEach(part => {
          // Clean up survey number (remove special chars but keep meaningful ones)
          const cleaned = part.replace(/[@\[\]]/g, '').trim();
          if (cleaned) uniqueSurveys.add(cleaned);
        });
      }
    });

    // Sort survey numbers intelligently (201, 201A, 201B, 202, etc.)
    const surveyList = Array.from(uniqueSurveys).sort((a, b) => {
      // Extract base number and suffix
      const getBaseSuffix = (str) => {
        const match = str.match(/^(\d+)(.*)$/);
        if (match) {
          return {
            base: parseInt(match[1]),
            suffix: match[2].trim()
          };
        }
        return { base: 0, suffix: str };
      };
      
      const aData = getBaseSuffix(a);
      const bData = getBaseSuffix(b);
      
      // First sort by base number
      if (aData.base !== bData.base) {
        return aData.base - bData.base;
      }
      
      // Then sort by suffix alphabetically
      return aData.suffix.localeCompare(bData.suffix);
    });
    
    setSurveyNumbers(surveyList);
    
    // Check if coming back from display with previous selections
    if (location.state?.previousSelections && location.state.previousSelections.length > 0) {
      const previousSet = new Set(location.state.previousSelections);
      setSelectedSurveys(previousSet);
      setSelectAll(previousSet.size === surveyList.length);
      // Store active filters to show visual indicator
      setActiveFilters(previousSet);
    } else {
      // Select all by default for first time
      setSelectedSurveys(new Set(surveyList));
      setSelectAll(true);
      setActiveFilters(new Set());
    }
  };

  const handleToggleSurvey = (survey) => {
    const newSelected = new Set(selectedSurveys);
    if (newSelected.has(survey)) {
      newSelected.delete(survey);
    } else {
      newSelected.add(survey);
    }
    setSelectedSurveys(newSelected);
    setSelectAll(newSelected.size === surveyNumbers.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSurveys(new Set());
      setSelectAll(false);
    } else {
      setSelectedSurveys(new Set(surveyNumbers));
      setSelectAll(true);
    }
  };

  const handleContinue = () => {
    if (selectedSurveys.size === 0) {
      alert('Please select at least one survey number');
      return;
    }

    // Filter transactions based on selected survey numbers
    const filteredTransactions = ecData.transactions.filter(txn => {
      const survey = txn.survey_number;
      if (!survey || survey === 'N/A' || survey === null) return false;
      
      // Check if any selected survey number is in this transaction's survey field
      return Array.from(selectedSurveys).some(selectedSurvey => 
        survey.includes(selectedSurvey)
      );
    });

    // Navigate to display with filtered data
    navigate(`/ec-display/${ecId}`, {
      state: {
        filteredTransactions,
        selectedSurveys: Array.from(selectedSurveys),
        originalData: ecData
      }
    });
  };

  const getTransactionCount = (survey) => {
    return ecData?.transactions.filter(txn => 
      txn.survey_number && txn.survey_number.includes(survey)
    ).length || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading EC data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <ArrowLeft className="w-8 h-8" />
            <h2 className="text-xl font-bold">Error</h2>
          </div>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/ec-extractor')}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Back to EC Extractor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/ec-extractor')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            
            <div>
              <h1 className="text-xl font-bold text-gray-900">Select Survey Numbers</h1>
              <p className="text-sm text-gray-500">Filter transactions by survey number</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center space-x-3 mb-6">
            <Filter className="w-8 h-8 text-green-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Filter by Survey Number</h2>
              <p className="text-gray-600">Select survey numbers to view related transactions only</p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Found {surveyNumbers.length} unique survey numbers</strong> across {ecData?.transactions?.length || 0} transactions.
              {location.state?.previousSelections && location.state.previousSelections.length > 0 ? (
                <span className="block mt-1">
                  🔄 <strong>Previous selection restored:</strong> {location.state.previousSelections.length} survey number(s) selected
                </span>
              ) : (
                <span className="block mt-1">Select the survey numbers you want to analyze.</span>
              )}
            </p>
          </div>

          {/* Select All */}
          <div className="mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={handleSelectAll}
              className="flex items-center space-x-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors w-full"
            >
              {selectAll ? (
                <CheckSquare className="w-6 h-6 text-green-600" />
              ) : (
                <Square className="w-6 h-6 text-gray-400" />
              )}
              <span className="font-semibold text-gray-900">
                {selectAll ? 'Deselect All' : 'Select All'} ({surveyNumbers.length} survey numbers)
              </span>
            </button>
          </div>

          {/* Survey Number List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6 max-h-96 overflow-y-auto">
            {surveyNumbers.map((survey) => {
              const isSelected = selectedSurveys.has(survey);
              const isCurrentlyActive = activeFilters.has(survey);
              const count = getTransactionCount(survey);
              
              return (
                <button
                  key={survey}
                  onClick={() => handleToggleSurvey(survey)}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {isSelected ? (
                      <CheckSquare className="w-5 h-5 text-green-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                    <div className="text-left">
                      <div className="flex items-center space-x-2">
                        <p className="font-semibold text-gray-900">Survey #{survey}</p>
                        {isCurrentlyActive && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                            Currently Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{count} transaction{count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selection Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Selected Survey Numbers:</p>
                <p className="text-2xl font-bold text-green-600">{selectedSurveys.size}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Transactions to Display:</p>
                <p className="text-2xl font-bold text-blue-600">
                  {ecData?.transactions.filter(txn => 
                    Array.from(selectedSurveys).some(s => txn.survey_number?.includes(s))
                  ).length || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => navigate('/ec-extractor')}
              className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              disabled={selectedSurveys.size === 0}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <span>Continue to Report</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SurveySelectionPage;
